import { isColorByOption } from '@paragon/contract-library';
import { enqueueSuccess } from '@paragon/notification-tools';

import { stringifyUrlPattern } from '../../urlPattern';
import { getAnalogModelForActiveComponentDesign } from '../selectors/getAnalogModelForActiveComponentDesignSelector';
import {
  getFirstSelectedItemEntryOrNull,
  getSingleSelectedItemEntryOrNull,
} from '../selectors/getSelectedItemSelector';
import { setDimensionMode } from '../slices/applicationSlice';
import { cameraSlice } from '../slices/camera/cameraSlice';
import { getCurrentCameraTypeAndProperties } from '../slices/camera/selectors';
import {
  checkResultPanelExpandedItemIdsSelect,
  componentPanelShowSet,
  loadCasePanelExpandedItemIdsSelect,
  loadCasePanelShowSet,
} from '../slices/panelDataSlice';
import { selectSnaps } from '../slices/selectionsSlice';
import { setColorBy, setShowAnalogModel } from '../slices/visualOptionsSlice';
import { ThunkActionVoid } from './thunkActions';

export const updateUrlFromState = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const selectedEntry = getFirstSelectedItemEntryOrNull(state);
  const selectedItemGuid = selectedEntry?.item.guid ?? null;

  dispatch(
    updateUrl(
      state.selections.activeComponentDesignGuid,
      selectedEntry?.type ?? null,
      selectedItemGuid,
      state.selections.selectedCheckResultsLoadCaseGuid,
      state.analysisSets.activeAnalysisSetGuid ?? null,
    ),
  );
};

export const updateUrl =
  (
    activeComponentDesignGuid: string | null,
    selectedItemType: string | null,
    selectedItemGuid: string | null,
    selectedLoadCaseGuid: string | null,
    selectedAnalysisSetGuid: string | null,
  ): ThunkActionVoid =>
  (_, getState) => {
    const { application } = getState();

    let collectionIdUrlParameter;
    switch (application.openContext?.type) {
      case 'project':
        collectionIdUrlParameter = application.openContext.projectGuid;
        break;

      case 'job':
        collectionIdUrlParameter = `${application.openContext.jobId}`;
        break;

      case 'shared-component-set':
        collectionIdUrlParameter = application.openContext.sharedComponentSetLookupCode;
        break;

      default:
        collectionIdUrlParameter = '0';
        break;
    }

    const newUrlParameters =
      selectedItemGuid && selectedItemType
        ? {
            projectGuid: collectionIdUrlParameter,
            activeComponentDesignGuid: activeComponentDesignGuid ?? '0',
            selectedItemType,
            selectedItemGuid,
            selectedLoadCaseGuid: selectedLoadCaseGuid ?? '0',
          }
        : {
            projectGuid: collectionIdUrlParameter,
          };

    let newPath = stringifyUrlPattern(newUrlParameters);

    const oldQueryParams = new URLSearchParams(window.location.search);
    const newQueryParams = new URLSearchParams();

    if (application.organizationGuid && application.customerGuid) {
      newQueryParams.set('organizationGuid', application.organizationGuid);
      newQueryParams.set('customerGuid', application.customerGuid);
    }

    if (selectedAnalysisSetGuid && oldQueryParams.has('analysisSetGuid')) {
      newQueryParams.set('analysisSetGuid', selectedAnalysisSetGuid);
    }

    switch (application.openContext?.type) {
      case 'station':
      case 'schedule-station':
        newQueryParams.set('station', application.openContext.stationGuid);
        break;
    }

    // the `jwt` param is used for revocable standalone tokens (generated for assembly stations)
    if (oldQueryParams.has('jwt')) {
      newQueryParams.set('jwt', oldQueryParams.get('jwt')!);
    }

    const paramsString = newQueryParams.toString();
    if (paramsString.length > 0) {
      newPath += `?${paramsString}`;
    }

    if (newPath !== window.location.pathname) {
      history.replaceState(null, '', newPath);
    }
  };

export const loadOptionalUserLayout =
  (params: Record<string, string>): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    let position = null;

    if (params.cameraLocation != null) {
      const parsedList = params.cameraLocation.split('_');
      if (
        parsedList.length === 6 &&
        !Number.isNaN(parseFloat(parsedList[0])) &&
        !Number.isNaN(parseFloat(parsedList[1])) &&
        !Number.isNaN(parseFloat(parsedList[2])) &&
        !Number.isNaN(parseFloat(parsedList[3])) &&
        !Number.isNaN(parseFloat(parsedList[4])) &&
        !Number.isNaN(parseFloat(parsedList[5]))
      ) {
        position = {
          x: parseFloat(`${parsedList[0]}.${parsedList[1]}`),
          y: parseFloat(`${parsedList[2]}.${parsedList[3]}`),
          z: parseFloat(`${parsedList[4]}'.'${parsedList[5]}`),
        };
      }
    }

    if (params.showAnalogModel != null) {
      dispatch(setShowAnalogModel(true));
    }

    const colorByParam = params.colorBy || params.heatmap;
    if (isColorByOption(colorByParam)) {
      dispatch(setColorBy(colorByParam));
    }

    if (params.showLoadCasePanel === 'true') {
      dispatch(loadCasePanelShowSet(true));
    }

    if (params.showComponentsPanel === 'true') {
      dispatch(componentPanelShowSet(true));
    }

    if (params.checkResultExpandedKeys != null) {
      dispatch(checkResultPanelExpandedItemIdsSelect(params.checkResultExpandedKeys.split('_')));
    }

    if (params.componentExpandedKeys != null) {
      // Unimplemented
    }

    if (params.loadCaseExpandedKeys != null) {
      dispatch(loadCasePanelExpandedItemIdsSelect(params.loadCaseExpandedKeys.split('_')));
    }

    let rotation = null;

    if (params.cameraRotation != null) {
      const parsedList = params.cameraRotation.split('_');
      if (
        parsedList.length === 8 &&
        !Number.isNaN(parseFloat(parsedList[0])) &&
        !Number.isNaN(parseFloat(parsedList[1])) &&
        !Number.isNaN(parseFloat(parsedList[2])) &&
        !Number.isNaN(parseFloat(parsedList[3])) &&
        !Number.isNaN(parseFloat(parsedList[4])) &&
        !Number.isNaN(parseFloat(parsedList[5])) &&
        !Number.isNaN(parseFloat(parsedList[6])) &&
        !Number.isNaN(parseFloat(parsedList[7]))
      ) {
        rotation = {
          x: parseFloat(`${parsedList[0]}.${parsedList[1]}`),
          y: parseFloat(`${parsedList[2]}.${parsedList[3]}`),
          z: parseFloat(`${parsedList[4]}.${parsedList[5]}`),
          w: parseFloat(`${parsedList[6]}.${parsedList[7]}`),
        };
      }
    }

    let zoom = null;

    if (params.cameraZoom != null) {
      const parsedList = params.cameraZoom.split('_');
      if (
        parsedList.length === 2 &&
        !Number.isNaN(parseFloat(parsedList[0])) &&
        !Number.isNaN(parseFloat(parsedList[1]))
      ) {
        zoom = parseFloat(`${parsedList[0]}.${parsedList[1]}`);
      }
    }

    if (params.selectedSnap1 != null && params.selectedSnap2 != null) {
      const parsedList1 = params.selectedSnap1.split('_');
      if (
        parsedList1.length === 6 &&
        !Number.isNaN(parseFloat(parsedList1[0])) &&
        !Number.isNaN(parseFloat(parsedList1[1])) &&
        !Number.isNaN(parseFloat(parsedList1[2])) &&
        !Number.isNaN(parseFloat(parsedList1[3])) &&
        !Number.isNaN(parseFloat(parsedList1[4])) &&
        !Number.isNaN(parseFloat(parsedList1[5]))
      ) {
        const parsedList2 = params.selectedSnap2.split('_');
        if (
          parsedList2.length === 6 &&
          !Number.isNaN(parseFloat(parsedList2[0])) &&
          !Number.isNaN(parseFloat(parsedList2[1])) &&
          !Number.isNaN(parseFloat(parsedList2[2])) &&
          !Number.isNaN(parseFloat(parsedList2[3])) &&
          !Number.isNaN(parseFloat(parsedList2[4])) &&
          !Number.isNaN(parseFloat(parsedList2[5]))
        ) {
          dispatch(
            selectSnaps({
              snap1: {
                x: parseFloat(`${parsedList1[0]}.${parsedList1[1]}`),
                y: parseFloat(`${parsedList1[2]}.${parsedList1[3]}`),
              },
              snap2: {
                x: parseFloat(`${parsedList2[0]}.${parsedList2[1]}`),
                y: parseFloat(`${parsedList2[2]}.${parsedList2[3]}`),
              },
            }),
          );
        }
      }
    }

    if (params.checkResultSort != null) {
      // Unimplemented
    }

    if (!state.application.inDimensionMode && params.inDimensionMode === 'true') {
      dispatch(setDimensionMode(true));
    }

    const [currentCameraType, currentCameraProperties] = getCurrentCameraTypeAndProperties(getState());

    // Only the design camera supports url actions at the moment.
    if (
      params.cameraType === 'orthographic-design' ||
      // `renderMode` is a legacy param used by some existing links, and 'Design' is the `renderMode` that maps to the
      //   current orthographic-design camera. Don't remove this case without probable cause.
      params.renderMode === 'Design'
    ) {
      if (currentCameraType !== 'orthographic-design') {
        dispatch(cameraSlice.actions.activeCameraChanged('orthographic-design'));
      }

      if (currentCameraProperties) {
        zoom ??= currentCameraProperties.zoom;
        position ??= currentCameraProperties.position;
        rotation ??= currentCameraProperties.rotation;
      }

      const target = null; // the design camera does not have a target

      if (position && rotation && zoom) {
        dispatch(
          cameraSlice.actions.cameraAltered({
            type: 'orthographic-design',
            properties: { zoom, position, rotation, target },
          }),
        );
      }
    }
  };

export const outputLink =
  (url: string): ThunkActionVoid | any =>
  (dispatch: any) =>
    (navigator as any).clipboard.writeText(url).then(
      () => dispatch(enqueueSuccess('Link copied to clipboard!')),
      () => window.prompt('Copy to clipboard: Ctrl+C, Enter', url),
    );

export const generateShareLink = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const selectedEntry = getSingleSelectedItemEntryOrNull(state);
  const selectedItemGuid = selectedEntry?.item.guid ?? null;

  const newUrlParameters = {
    projectGuid: state.application.openContext?.type === 'project' ? state.application.openContext.projectGuid : '0',
    activeComponentDesignGuid: state.selections.activeComponentDesignGuid,
    selectedItemType: selectedEntry?.type ?? null,
    selectedItemGuid,
    selectedLoadCaseGuid:
      state.selections.selectedCheckResultsLoadCaseGuid === null
        ? '0'
        : state.selections.selectedCheckResultsLoadCaseGuid,
  };
  let queryString = '?=';

  const [cameraType, cameraProperties] = getCurrentCameraTypeAndProperties(state);

  if (cameraType === 'orthographic-design' && cameraProperties) {
    queryString += `cameraType/${cameraType}`;

    const cameraPositionValues = [
      cameraProperties.position.x.toFixed(2).split('.')[0],
      cameraProperties.position.x.toFixed(2).split('.')[1],
      cameraProperties.position.y.toFixed(2).split('.')[0],
      cameraProperties.position.y.toFixed(2).split('.')[1],
      cameraProperties.position.z.toFixed(2).split('.')[0],
      cameraProperties.position.z.toFixed(2).split('.')[1],
    ];
    queryString += `/cameraLocation/${cameraPositionValues.join('_')}`;

    const cameraRotationValues = [
      cameraProperties.rotation.x.toFixed(2).split('.')[0],
      cameraProperties.rotation.x.toFixed(2).split('.')[1],
      cameraProperties.rotation.y.toFixed(2).split('.')[0],
      cameraProperties.rotation.y.toFixed(2).split('.')[1],
      cameraProperties.rotation.z.toFixed(2).split('.')[0],
      cameraProperties.rotation.z.toFixed(2).split('.')[1],
      cameraProperties.rotation.w.toFixed(2).split('.')[0],
      cameraProperties.rotation.w.toFixed(2).split('.')[1],
    ];
    queryString += `/cameraRotation/${cameraRotationValues.join('_')}`;

    const cameraZoomValues = [
      cameraProperties.zoom.toFixed(2).split('.')[0],
      cameraProperties.zoom.toFixed(2).split('.')[1],
    ];
    queryString += `/cameraZoom/${cameraZoomValues.join('_')}`;
  }

  if (state.visualOptions.showAnalogModel) {
    const analogModel = getAnalogModelForActiveComponentDesign(state);
    if (analogModel != null) {
      const analogModelGuid = analogModel.guid;
      queryString += `/showAnalogModel/${analogModelGuid}`;
    }
  }

  queryString += `/inDimensionMode/${state.application.inDimensionMode.toString()}`;
  queryString += `/colorBy/${state.visualOptions.colorBy}`;
  queryString += `/showLoadCasePanel/${state.panelData.loadCasePanelOpen}`;
  queryString += `/showComponentsPanel/${state.panelData.navigationPanelOpen}`;

  if (state.panelData.checkResultExpandedItemIds.length > 0) {
    queryString += `/checkResultExpandedKeys/${state.panelData.checkResultExpandedItemIds.join('_')}`;
  }
  if (state.panelData.loadCaseExpandedItemIds.length > 0) {
    queryString += `/loadCaseExpandedKeys/${state.panelData.loadCaseExpandedItemIds.join('_')}`;
  }

  if (state.selections.selectedSnaps.snap1 !== null && state.selections.selectedSnaps.snap2 !== null) {
    const selectedSnap1 = state.selections.selectedSnaps.snap1;
    const selectedSnap1Values = [
      selectedSnap1.x.toFixed(2).split('.')[0],
      selectedSnap1.x.toFixed(2).split('.')[1],
      selectedSnap1.y.toFixed(2).split('.')[0],
      selectedSnap1.y.toFixed(2).split('.')[1],
    ];
    queryString += `/selectedSnap1${selectedSnap1Values.join('_')}`;

    const selectedSnap2 = state.selections.selectedSnaps.snap2;
    const selectedSnap2Values = [
      selectedSnap2.x.toFixed(2).split('.')[0],
      selectedSnap2.x.toFixed(2).split('.')[1],
      selectedSnap2.y.toFixed(2).split('.')[0],
      selectedSnap2.y.toFixed(2).split('.')[1],
    ];
    queryString += `/selectedSnap2/${selectedSnap2Values.join('_')}`;
  }

  dispatch(outputLink(window.location.host + stringifyUrlPattern(newUrlParameters) + queryString));
};
